/**
 * Session cache of the user's full weight history for per-day target
 * backfill (4.34) — mirrors mobile services/weightHistoryCache.ts.
 * `get()` answers null while loading / stale; persisting callers must wait.
 */

import { useEffect, useState } from 'react'
import { fetchAllWeightPoints } from '@/lib/firestore/progress'
import { appEvents, WEIGHT_HISTORY_CHANGED } from '@/lib/events'
import type { WeightPoint } from '@/kidney/dailyTargets'

type Listener = () => void

class WeightHistoryCacheClass {
  private uid: string | null = null
  private points: WeightPoint[] | null = null
  private stale = true
  private inflight: Promise<void> | null = null
  private generation = 0
  private listeners = new Set<Listener>()

  constructor() {
    appEvents.on(WEIGHT_HISTORY_CHANGED, () => this.invalidate())
  }

  get(uid: string): WeightPoint[] | null {
    if (this.uid !== uid) {
      this.uid = uid
      this.points = null
      this.stale = true
      this.generation += 1
    }
    if (this.stale) {
      void this.load(uid).catch(() => {})
      return null
    }
    return this.points
  }

  async ensure(uid: string): Promise<WeightPoint[]> {
    const cached = this.get(uid)
    if (cached) return cached
    await this.load(uid)
    if (this.uid !== uid) throw new Error('Weight history account changed during load')
    if (this.stale) return this.ensure(uid)
    return this.points!
  }

  private load(uid: string): Promise<void> {
    if (this.inflight) return this.inflight
    const gen = this.generation
    let succeeded = false
    this.inflight = fetchAllWeightPoints(uid)
      .then((points) => {
        succeeded = true
        if (gen !== this.generation || this.uid !== uid) return
        this.points = points
        this.stale = false
      })
      .catch((err) => {
        console.warn('Weight history load failed:', err)
        throw err
      })
      .finally(() => {
        this.inflight = null
        // A notification here after failure immediately re-enters get() in
        // every subscribed hook and would create an unbounded retry loop.
        if (succeeded || gen !== this.generation) this.notify()
      })
    return this.inflight
  }

  invalidate(): void {
    this.stale = true
    this.generation += 1
    this.notify()
  }

  clear(): void {
    this.uid = null
    this.points = null
    this.stale = true
    this.generation += 1
    this.notify()
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  private notify(): void {
    this.listeners.forEach((fn) => fn())
  }
}

export const WeightHistoryCache = new WeightHistoryCacheClass()

/** React view: the points, or null while loading / stale. */
export function useWeightHistory(uid: string | undefined): WeightPoint[] | null {
  const [state, setState] = useState(() => ({ uid, points: uid ? WeightHistoryCache.get(uid) : null }))
  useEffect(() => {
    if (!uid) {
      setState({ uid, points: null })
      return
    }
    const read = () => setState({ uid, points: WeightHistoryCache.get(uid) })
    read()
    const unsubscribe = WeightHistoryCache.subscribe(read)
    const visible = () => { if (document.visibilityState === 'visible') read() }
    document.addEventListener('visibilitychange', visible)
    return () => { unsubscribe(); document.removeEventListener('visibilitychange', visible) }
  }, [uid])
  return state.uid === uid ? state.points : null
}
